<template>
  <div class="container">
    <el-card :body-style="containerStyles">
      <template>
        <div
          class="metric-container"
          :class="{
            'metric-container-samples': m.click
          }"
          v-for="(m, i) in visibleMetrics"
          :key="i"
        >
          <p
            class="metric-title"
            :data-qa="
              `metric-title-${m.title.replace(/\s+/g, '-').toLowerCase()}`
            "
          >
            {{ m.title }}
          </p>
          <p
            class="metric-value"
            :data-qa="
              `metric-value-${m.title.replace(/\s+/g, '-').toLowerCase()}`
            "
          >
            {{ m.value }}
          </p>
          <el-button v-if="m.click" @click="m.click" type="primary" plain>
            <i :class="m.clickIcon"></i>
            {{ m.clickTitle }}
          </el-button>
        </div>
      </template>
    </el-card>
  </div>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  props: {
    type: {
      required: true,
      type: String
    },
    metrics: {
      required: true,
      type: Object
    },
    columns: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      containerStyles: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridTemplateColumns: `repeat(${this.columns}, 1fr)`,
        justifyContent: 'left',
        padding: '0'
      }
    }
  },
  methods: {
    recalculateExactSize() {
      EventBus.$emit('recalculate-exact-size')
    },
    numberWithCommas(num) {
      if (!num) {
        return num
      }
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },
  computed: {
    includeProfileMetric() {
      return (
        this.type === 'users' || this.type === 'size' || this.type === 'all'
      )
    },

    includeDeviceMetrics() {
      return (
        this.type === 'devices' || this.type === 'size' || this.type === 'all'
      )
    },

    includeChannels() {
      return this.type === 'channels'
    },

    includeRolling() {
      return this.type === 'rolling'
    },

    includeSamplingWarning() {
      return this.metrics.sampling_level
    },

    visibleMetrics() {
      const result = []

      if (this.includeProfileMetric) {
        result.push({
          title: this.$t('Audiences_Segment_Metric_UserProfiles'),
          value: this.numberWithCommas(this.metrics.users) || 0
        })
      }

      if (this.includeDeviceMetrics) {
        if ($hasFeature('iosPush')) {
          result.push({
            title: this.$t('Audiences_Segment_Metric_iOsDevices'),
            value: this.numberWithCommas(this.metrics.devices_ios_active) || 0
          })
        }

        if ($hasFeature('androidPush')) {
          result.push({
            title: this.$t('Audiences_Segment_Metric_AndroidDevices'),
            value:
              this.numberWithCommas(this.metrics.devices_android_active) || 0
          })
        }

        if ($hasFeature('webPush')) {
          result.push({
            title: this.$t('Audiences_Segment_Metric_WebDevices'),
            value: this.numberWithCommas(this.metrics.devices_web_active) || 0
          })
        }
      }

      if (this.includeChannels) {
        if ($hasFeature('email')) {
          result.push({
            title: this.$t('Audiences_Segment_ChannelReach_EmailableUsers'),
            value: this.numberWithCommas(this.metrics.users_emailable) || 0
          })
        }

        if ($hasFeature('sms')) {
          result.push({
            title: this.$t('Audiences_Segment_ChannelReach_SmsUsers'),
            value: this.numberWithCommas(this.metrics.users_sms) || 0
          })
        }

        if ($hasFeature('androidPush')) {
          result.push({
            title: this.$t('Audiences_Segment_ChannelReach_AndroidPushable'),
            value:
              this.numberWithCommas(this.metrics.devices_android_pushable) || 0
          })
        }

        if ($hasFeature('iosPush')) {
          result.push({
            title: this.$t('Audiences_Segment_ChannelReach_iosPushable'),
            value: this.numberWithCommas(this.metrics.devices_ios_pushable) || 0
          })
        }

        if ($hasFeature('webPush')) {
          result.push({
            title: this.$t('Audiences_Segment_ChannelReach_WebPushable'),
            value: this.numberWithCommas(this.metrics.devices_web_pushable) || 0
          })
        }
      }

      if (this.includeRolling) {
        result.push({
          title: this.$t('Audiences_Segment_RollingSync_DevicesAdded'),
          value: this.numberWithCommas(this.metrics.devices_added) || 0
        })

        result.push({
          title: this.$t('Audiences_Segment_RollingSync_DevicesRemoved'),
          value: this.numberWithCommas(this.metrics.devices_removed) || 0
        })

        result.push({
          title: this.$t('Audiences_Segment_RollingSync_ProfilesAdded'),
          value: this.numberWithCommas(this.metrics.profiles_added) || 0
        })

        result.push({
          title: this.$t('Audiences_Segment_RollingSync_ProfilesRemoved'),
          value: this.numberWithCommas(this.metrics.profiles_removed) || 0
        })
      }

      if (this.includeSamplingWarning) {
        result.push({
          title: this.$t('Audiences_Segment_Sampling_CountIsEstimated'),
          click: this.recalculateExactSize,
          clickIcon: 'el-icon-refresh',
          clickTitle: this.$t('Audiences_Segment_Sampling_GetExactResults')
        })
      }

      return result
    }
  }
}
</script>

<style scoped>
.container {
  position: relative;
}
.metric-container {
  width: 168px;
  background: #f8fafc;
  border-radius: 4px;
  flex: none;
  order: 0;
  align-self: flex-start;
  margin: 10px;
  display: flex;
  flex-direction: column;
  padding: 15px;
}
.metric-title {
  font-size: 12px;
  line-height: 18px;
  color: #7e8596;
}
.metric-value {
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #4b515f;
}
.metric-container-samples {
  grid-column-end: -1;
  background: unset;

  margin: 10px;
  padding: 8px 0;
}
.metric-container-samples .metric-title {
  font-size: 12px;
  line-height: 18px;
  color: #7e8596;
  margin-bottom: 8px;
}
.container {
  padding: 0 20px 20px 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/audiences/components/SegmentCountsTopBar.vue